import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, ExternalLink, FileText, Loader2, Lock, RefreshCw, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

// ============================================================================
// PanelProduccionCFDIEntidad — jun-2026. El encendido de producción CFDI y la
// Carta Manifiesto viven en la ficha de CADA entidad (antes estaban en bloque
// dentro de /admin-saas). Reglas duras que se conservan:
//   - Solo super_admin (el backend responde 403 real, no es un oculto de UI).
//   - El sistema INFORMA advertencias pero NO bloquea: el super_admin decide.
//   - La confirmación explícita por CFDI (checkbox + tecleo "TIMBRAR") vive en
//     el flujo de emisión y NO se sustituye desde aquí.
//   - Motivo OBLIGATORIO al cambiar de modo; queda en bitácora con estado
//     anterior, estado nuevo, usuario y timestamp.
// ============================================================================

const CHIP_VERIF = {
  vigente: "bg-emerald-100 text-emerald-800 border-emerald-300",
  firmada: "bg-emerald-100 text-emerald-800 border-emerald-300",
  por_vencer: "bg-amber-100 text-amber-800 border-amber-300",
  pendiente: "bg-amber-100 text-amber-800 border-amber-300",
  vencido: "bg-red-100 text-red-800 border-red-300",
  sin_certificado: "bg-red-100 text-red-800 border-red-300",
  no_verificado: "bg-slate-100 text-slate-700 border-slate-300",
  sin_organizacion: "bg-slate-100 text-slate-500 border-slate-300",
};

const ChipVerif = ({ estatus, testid }) => (
  <span data-testid={testid}
        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${CHIP_VERIF[estatus] || CHIP_VERIF.no_verificado}`}>
    {estatus || "no_verificado"}
  </span>
);

const ChipModo = ({ item }) => {
  const enProd = item.modo_operacion === "produccion";
  if (enProd && item.emision_autorizada) {
    return (
      <span data-testid="entidad-cfdi-estado-produccion-autorizada"
            className="inline-flex items-center gap-1 rounded-full border border-emerald-400 bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-900">
        <Zap className="h-3 w-3" /> PRODUCCIÓN · autorizada
      </span>
    );
  }
  if (enProd) {
    return (
      <span data-testid="entidad-cfdi-estado-produccion-bloqueada"
            className="inline-flex items-center gap-1 rounded-full border border-amber-400 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-900">
        <Lock className="h-3 w-3" /> PRODUCCIÓN · emisión bloqueada
      </span>
    );
  }
  return (
    <span data-testid="entidad-cfdi-estado-sandbox"
          className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
      <Lock className="h-3 w-3" /> Interno (sandbox)
    </span>
  );
};

export const PanelProduccionCFDIEntidad = ({ entityId }) => {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nuevoModo, setNuevoModo] = useState("sandbox");
  const [nuevaEmision, setNuevaEmision] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [confirmado, setConfirmado] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [sklive, setSklive] = useState("");
  const [motivoLlave, setMotivoLlave] = useState("");
  const [enviandoLlave, setEnviandoLlave] = useState(false);
  const [errorLlave, setErrorLlave] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/sofom/cfdi/entidades/${entityId}/produccion`);
      setItem(data);
    } catch (e) {
      toast.error(apiError(e, "No se pudo cargar el estado de producción CFDI."));
      setItem(null);
    } finally { setLoading(false); }
  }, [entityId]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrir = () => {
    setNuevoModo(item?.modo_operacion || "sandbox");
    setNuevaEmision(!!item?.emision_autorizada);
    setMotivo("");
    setConfirmado(false);
    setSklive("");
    setMotivoLlave("");
    setErrorLlave(null);
    setDialogOpen(true);
  };

  const cerrar = () => {
    setDialogOpen(false);
    setMotivo("");
    setConfirmado(false);
    setSklive("");
    setMotivoLlave("");
    setErrorLlave(null);
  };

  const hayCambios = !!item && (
    nuevoModo !== item.modo_operacion || nuevaEmision !== !!item.emision_autorizada
  );

  const guardar = async () => {
    if (!item) return;
    if (!hayCambios) { toast.info("No hay cambios que guardar."); return; }
    if (!motivo.trim()) { toast.error("El motivo es obligatorio."); return; }
    if (!confirmado) { toast.error("Confirma que entiendes las implicaciones."); return; }
    setGuardando(true);
    try {
      const cambios = [];
      if (nuevoModo !== item.modo_operacion) {
        await api.put("/sofom/cfdi/modo-operacion",
                      { entity_id: item.entity_id, modo: nuevoModo, motivo: motivo.trim() });
        cambios.push(`modo: ${item.modo_operacion} → ${nuevoModo}`);
      }
      // Pasar a producción resetea `emision_autorizada` a false en backend.
      const emisionTrasModo = (nuevoModo !== item.modo_operacion && nuevoModo === "produccion")
        ? false : !!item.emision_autorizada;
      if (nuevaEmision !== emisionTrasModo) {
        await api.put("/sofom/cfdi/emision-autorizada",
                      { entity_id: item.entity_id, autorizada: nuevaEmision, motivo: motivo.trim() });
        cambios.push(`emisión: ${emisionTrasModo ? "autorizada" : "bloqueada"} → ${nuevaEmision ? "autorizada" : "bloqueada"}`);
      }
      toast.success(`Cambios guardados (${cambios.join(", ")}).`);
      cerrar();
      await cargar();
    } catch (e) {
      toast.error(apiError(e, "No se pudieron guardar los cambios."));
    } finally { setGuardando(false); }
  };

  const sklivePrefijoValido = sklive.startsWith("sk_live_") && sklive.length >= 20;

  const guardarSkLive = async () => {
    if (!item) return;
    if (!sklivePrefijoValido) {
      toast.error("La llave debe empezar con 'sk_live_' y tener al menos 20 caracteres.");
      return;
    }
    if (!motivoLlave.trim()) { toast.error("Motivo obligatorio para registrar en bitácora."); return; }
    setEnviandoLlave(true);
    setErrorLlave(null);
    try {
      const { data: res } = await api.put(
        `/admin/sofom/cfdi/entidades/${item.entity_id}/secret-key-produccion`,
        { secret_key: sklive.trim(), motivo: motivoLlave.trim() }
      );
      toast.success(`Llave sk_live_ guardada · mask ${res.llave_mask || "…"}.${res.rfc_advertencia ? ` ⚠ ${res.rfc_advertencia}` : ""}`);
      setSklive("");
      setMotivoLlave("");
      await cargar();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let mensaje = "No se pudo guardar la llave.";
      let orgReal = null;
      if (typeof detail === "string") { mensaje = detail; }
      else if (detail && typeof detail === "object") {
        mensaje = detail.mensaje || mensaje;
        orgReal = detail.org_real || null;
      }
      setErrorLlave({ mensaje, orgReal });
      toast.error(mensaje);
    } finally { setEnviandoLlave(false); }
  };

  if (loading && !item) {
    return (
      <section data-testid="entidad-cfdi-produccion-loading"
               className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-5 text-sm text-slate-500 shadow-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Consultando estado de producción CFDI…
      </section>
    );
  }
  if (!item) return null;

  const faltantes = item.faltantes || [];

  return (
    <>
      {/* ---------------- Encendido de producción CFDI ---------------- */}
      <section data-testid="entidad-cfdi-produccion"
               className="rounded-lg border border-amber-300 bg-white p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 className="flex items-center gap-1.5 font-heading text-sm font-semibold text-slate-700">
              <Zap className="h-4 w-4 text-amber-600" /> Encendido de producción CFDI
            </h4>
            <p className="mt-1 text-xs text-slate-500">
              Cambia el modo (interno/producción) y autoriza la emisión de esta entidad.
              Cada acción queda en bitácora con motivo, usuario y hora.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ChipModo item={item} />
            <Button size="sm" variant="ghost" onClick={cargar} disabled={loading}
                    data-testid="entidad-cfdi-refrescar">
              <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs sm:grid-cols-4">
          <div className="flex items-center gap-1.5">
            <FileText className="h-3 w-3 text-slate-500" /><span>CSD:</span>
            <ChipVerif estatus={item.csd_estatus} testid={`entidad-cfdi-csd-${item.csd_estatus}`} />
          </div>
          <div className="flex items-center gap-1.5">
            <FileText className="h-3 w-3 text-slate-500" /><span>Manifiesto:</span>
            <ChipVerif estatus={item.manifiesto_estatus}
                       testid={`entidad-cfdi-manif-${item.manifiesto_estatus}`} />
          </div>
          <div className="flex items-center gap-1.5">
            <FileText className="h-3 w-3 text-slate-500" /><span>Llave:</span>
            <span data-testid={`entidad-cfdi-llave-${item.llave_es_produccion ? "prod" : "no-prod"}`}
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] font-semibold ${
                    item.llave_es_produccion
                      ? "border-emerald-300 bg-emerald-100 text-emerald-800"
                      : item.llave_es_prueba
                        ? "border-amber-300 bg-amber-100 text-amber-800"
                        : "border-slate-300 bg-slate-100 text-slate-500"}`}>
              {item.llave_prefijo || "sin llave"}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span>Observaciones:</span>
            {faltantes.length === 0 ? (
              <span className="font-semibold text-emerald-700" data-testid="entidad-cfdi-obs-cero">
                ✓ ninguna
              </span>
            ) : (
              <span className="font-semibold text-amber-800" data-testid="entidad-cfdi-obs">
                {faltantes.length} obs.
              </span>
            )}
          </div>
        </div>

        {faltantes.length > 0 && (
          <ul data-testid="entidad-cfdi-obs-list"
              className="mt-2 ml-4 list-disc space-y-0.5 text-[11px] text-amber-900">
            {faltantes.map((f, i) => <li key={i}>{f}</li>)}
          </ul>
        )}

        {item.modo_cambiado_por && (
          <p className="mt-2 text-[11px] text-slate-500" data-testid="entidad-cfdi-ultimo-cambio">
            Último cambio de modo: {item.modo_cambiado_por}
            {item.modo_cambiado_at ? ` · ${new Date(item.modo_cambiado_at).toLocaleString("es-MX")}` : ""}
            {item.modo_cambiado_motivo ? ` · motivo: ${item.modo_cambiado_motivo}` : ""}
          </p>
        )}

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-start gap-1.5 text-[11px] text-slate-600">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-slate-500" />
            <p className="max-w-2xl">
              El sistema informa las advertencias pero <strong>no bloquea</strong>: el super_admin
              decide si enciende con observaciones. La confirmación explícita por CFDI
              (checkbox + tecleo &quot;TIMBRAR&quot;) sigue activa aunque la emisión esté autorizada.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={abrir}
                  data-testid="entidad-cfdi-configurar-btn">
            Configurar
          </Button>
        </div>
        {!item.organization_id && (
          <p className="mt-1 text-[11px] text-slate-500" data-testid="entidad-cfdi-sin-org">
            Aviso: la entidad todavía no tiene organización creada en Facturapi (paso 1 del
            wizard). Puedes cambiar el modo igualmente — el sistema informa, no bloquea.
          </p>
        )}
      </section>

      {/* ---------------- Carta Manifiesto ---------------- */}
      <section data-testid="entidad-cfdi-manifiesto-panel"
               className="rounded-lg border border-blue-200 bg-white p-5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 className="flex items-center gap-1.5 font-heading text-sm font-semibold text-slate-700">
              <FileText className="h-4 w-4 text-blue-600" /> Carta Manifiesto (RMF 2026)
            </h4>
            <p className="mt-1 text-xs text-slate-500">
              {item.leyenda_rmf}
            </p>
          </div>
          <ChipVerif estatus={item.manifiesto_estatus}
                     testid={`entidad-cfdi-manifiesto-estado-${item.manifiesto_estatus}`} />
        </div>
        <p className="text-xs text-slate-700" data-testid="entidad-cfdi-manifiesto-detalle">
          {item.manifiesto_detalle || "—"}
        </p>
        {(item.manifiesto_pending_steps || []).length > 0 && (
          <p className="mt-1 font-mono text-[10px] text-slate-500"
             data-testid="entidad-cfdi-manifiesto-pending">
            pending_steps: {(item.manifiesto_pending_steps || []).join(", ")}
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <a href={item.portal_firma_url} target="_blank" rel="noreferrer"
             data-testid="entidad-cfdi-manifiesto-portal"
             className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:underline">
            Portal de firma de Facturapi <ExternalLink className="h-3 w-3" />
          </a>
          {item.rfc && <span className="font-mono text-xs text-slate-500">RFC emisor: {item.rfc}</span>}
        </div>
        <div className="mt-3 flex items-start gap-1.5 rounded-md border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-600">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-slate-500" />
          <p>
            Fail-safe estricto: &quot;no verificado&quot; NO significa que esté firmada — significa
            que no se pudo confirmar. La e.firma NUNCA pasa por FELVORA.
          </p>
        </div>
      </section>

      {/* ---------------- Diálogo de configuración ---------------- */}
      <Dialog open={dialogOpen} onOpenChange={(v) => !v && cerrar()}>
        <DialogContent className="max-w-2xl" data-testid="entidad-cfdi-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Configurar producción CFDI</DialogTitle>
            <DialogDescription>
              {item.razon_social} · RFC {item.rfc || "—"}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
            <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                Verificaciones previas
              </p>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="flex items-center gap-1.5">
                  <span>CSD:</span>
                  <ChipVerif estatus={item.csd_estatus} testid="entidad-cfdi-dlg-csd" />
                </div>
                <div className="flex items-center gap-1.5">
                  <span>Manifiesto:</span>
                  <ChipVerif estatus={item.manifiesto_estatus} testid="entidad-cfdi-dlg-manif" />
                </div>
                <div className="flex items-center gap-1.5">
                  <span>Llave:</span>
                  <span className="font-mono text-[10px] font-semibold">
                    {item.llave_prefijo || "sin llave"}
                  </span>
                </div>
              </div>
              {faltantes.length > 0 && (
                <div data-testid="entidad-cfdi-dlg-faltantes"
                     className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-2">
                  <p className="text-[11px] font-semibold text-amber-900">
                    ⚠ Advertencias (puedes encender igualmente, quedan en bitácora):
                  </p>
                  <ul className="ml-4 list-disc space-y-0.5 text-[11px] text-amber-900">
                    {faltantes.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>
                </div>
              )}
            </div>

            {item.organization_id && !item.llave_es_produccion && (
              <div data-testid="entidad-cfdi-sklive-section"
                   className="space-y-2 rounded-md border border-amber-400 bg-amber-50 p-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-amber-900">
                    Falta la llave de producción (sk_live_)
                  </p>
                  <p className="mt-0.5 text-[11px] text-amber-800">
                    Hoy la entidad tiene una llave <span className="font-mono">{item.llave_prefijo || "sin llave"}</span>.
                    Pega la Secret Key <span className="font-mono">sk_live_*</span> de esta organización:
                    se valida contra Facturapi antes de guardarse y se almacena cifrada.
                  </p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="entidad-cfdi-input-sklive" className="text-[11px] font-semibold text-amber-900">
                    Secret Key sk_live_ (no se muestra en pantalla)
                  </Label>
                  <input id="entidad-cfdi-input-sklive" type="password"
                         data-testid="entidad-cfdi-sklive-input"
                         value={sklive}
                         onChange={(e) => { setSklive(e.target.value); setErrorLlave(null); }}
                         placeholder="sk_live_XXXXXXXXXXXXXXXXXXXX"
                         className="w-full rounded-md border border-amber-300 bg-white px-3 py-1.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-amber-400"
                         autoComplete="off" spellCheck={false} />
                  {sklive && !sklivePrefijoValido && (
                    <p data-testid="entidad-cfdi-sklive-prefix-warn" className="text-[11px] text-red-800">
                      La llave debe comenzar con <span className="font-mono">sk_live_</span> y tener al menos 20 caracteres.
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="entidad-cfdi-motivo-llave" className="text-[11px] font-semibold text-amber-900">
                    Motivo (queda en bitácora)
                  </Label>
                  <Textarea id="entidad-cfdi-motivo-llave" value={motivoLlave}
                            onChange={(e) => setMotivoLlave(e.target.value)}
                            placeholder="Ej. Alta autorizada por contrato firmado el DD/MM/AAAA."
                            className="min-h-[52px] text-xs"
                            data-testid="entidad-cfdi-sklive-motivo" />
                </div>
                {errorLlave && (
                  <div data-testid="entidad-cfdi-sklive-error"
                       className="space-y-1 rounded-md border border-red-300 bg-red-50 p-2 text-[11px] text-red-900">
                    <p className="font-semibold">Facturapi rechazó la llave:</p>
                    <p>{errorLlave.mensaje}</p>
                    {errorLlave.orgReal && (
                      <div className="mt-1 rounded-sm border border-red-200 bg-white p-1.5 font-mono text-[10px]">
                        Llave detectada en: RFC {errorLlave.orgReal.rfc || "—"} · id …{errorLlave.orgReal.org_id_suffix}
                        {errorLlave.orgReal.razon_social ? ` · ${errorLlave.orgReal.razon_social}` : ""}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-end">
                  <Button size="sm" onClick={guardarSkLive}
                          disabled={enviandoLlave || !sklivePrefijoValido || !motivoLlave.trim()}
                          data-testid="entidad-cfdi-sklive-guardar"
                          className="bg-amber-700 hover:bg-amber-800">
                    {enviandoLlave && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                    Guardar llave sk_live_
                  </Button>
                </div>
              </div>
            )}

            {item.organization_id && item.llave_es_produccion && (
              <div data-testid="entidad-cfdi-sklive-ok"
                   className="rounded-md border border-emerald-300 bg-emerald-50 p-2 text-[11px] text-emerald-900">
                <CheckCircle2 className="mr-1 inline h-3 w-3" />
                Llave de producción cargada (<span className="font-mono">{item.llave_prefijo}</span>).
                Facturapi confirmó que pertenece a esta organización.
              </div>
            )}

            <div className="rounded-md border border-slate-200 p-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-700">
                Estado actual
              </p>
              <ChipModo item={item} />
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Modo</Label>
                <RadioGroup value={nuevoModo} onValueChange={setNuevoModo} className="mt-1">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="sandbox" id="entidad-modo-sandbox"
                                    data-testid="entidad-cfdi-radio-sandbox" />
                    <Label htmlFor="entidad-modo-sandbox" className="cursor-pointer text-sm">
                      Interno (sandbox) — los CFDI NO se transmiten al SAT.
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="produccion" id="entidad-modo-produccion"
                                    data-testid="entidad-cfdi-radio-produccion" />
                    <Label htmlFor="entidad-modo-produccion" className="cursor-pointer text-sm">
                      <strong className="text-red-800">Producción</strong> — los CFDI se timbran ante el SAT y son fiscalmente reales.
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">
                  Autorización de emisión
                </Label>
                <RadioGroup value={nuevaEmision ? "autorizada" : "bloqueada"}
                            onValueChange={(v) => setNuevaEmision(v === "autorizada")}
                            className="mt-1">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="bloqueada" id="entidad-emi-bloqueada"
                                    data-testid="entidad-cfdi-radio-emi-bloq" />
                    <Label htmlFor="entidad-emi-bloqueada" className="cursor-pointer text-sm">
                      Bloqueada — nadie puede timbrar aunque intente.
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="autorizada" id="entidad-emi-autorizada"
                                    data-testid="entidad-cfdi-radio-emi-ok" />
                    <Label htmlFor="entidad-emi-autorizada" className="cursor-pointer text-sm">
                      Autorizada — se puede timbrar (con confirmación por factura).
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="entidad-cfdi-motivo-input" className="text-xs font-semibold uppercase tracking-wider">
                  Motivo del cambio (obligatorio)
                </Label>
                <Textarea id="entidad-cfdi-motivo-input" value={motivo}
                          onChange={(e) => setMotivo(e.target.value)}
                          placeholder="Ej. Contrato firmado, se autoriza timbrado productivo."
                          className="mt-1 text-sm"
                          data-testid="entidad-cfdi-motivo" />
                {!motivo.trim() && (
                  <p className="mt-1 text-[11px] text-red-700" data-testid="entidad-cfdi-motivo-requerido">
                    Sin motivo no se puede guardar: queda en bitácora junto al estado anterior y el nuevo.
                  </p>
                )}
              </div>

              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2">
                <Checkbox id="entidad-cfdi-confirmo-check" checked={confirmado}
                          onCheckedChange={setConfirmado}
                          data-testid="entidad-cfdi-confirmo" />
                <Label htmlFor="entidad-cfdi-confirmo-check" className="cursor-pointer text-xs text-red-900">
                  Entiendo las implicaciones fiscales y legales. Al autorizar, los CFDI emitidos
                  por esta entidad serán fiscalmente reales ante el SAT y quedará constancia de
                  que yo lo autoricé.
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={cerrar} disabled={guardando}
                    data-testid="entidad-cfdi-cancelar">Cancelar</Button>
            <Button onClick={guardar}
                    disabled={guardando || !hayCambios || !motivo.trim() || !confirmado}
                    data-testid="entidad-cfdi-guardar"
                    className="bg-red-700 hover:bg-red-800">
              {guardando && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PanelProduccionCFDIEntidad;
